import { RouteComponentProps } from 'react-router-dom';
import Page from '../../components/Page';
import PageLayout from '../../components/PageLayout/PageLayout';
import PageTitle from '../../components/PageLayout/PageTitle';
import useCatalogItems from '../../hooks/useCatalogItems';
import useCatalogLocations from '../../hooks/useCatalogLocations';
import { useCatalogs } from '../../hooks';
import LoadingModal from '@raydiant/raydial/components/LoadingModal';
import { capitalize } from '../../utilities/stringUtils';
import Card from '@raydiant/raydial/components/Card';
import Pagination from '@raydiant/raydial/components/Pagination';
import Text from '@raydiant/raydial/components/Text';
import { Basket } from '@raydiant/raydial/components/Icons';
import zeroItemsSvg from '../../assets/zero-items.svg';
import noItemsSvg from '../../assets/no-items.svg';
import rotateRightSvg from '../../assets/rotate-right.svg';
import { useEffect, useState } from 'react';
import PageSizeSelect from '../../components/PageSizeSelect/PageSizeSelect';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import moment from 'moment';

type RouteParams = {
  catalogId: string;
};

const ProductHubCatalogDetailsPage = (
  props: RouteComponentProps<RouteParams>,
) => {
  type Visibility = 'all' | 'visible' | 'kiosk' | 'menu';

  const { params } = props.match;
  const { catalogId } = params;
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [visibility, setVisibility] = useState<Visibility>('all');
  const [total, setTotal] = useState(0); // total num of products of this catalog.
  const [locationId, setLocationId] = useState<string | undefined>();

  const catalogsQuery = useCatalogs();
  const catalogLocationsQuery = useCatalogLocations(catalogId, {});

  let { data: catalogLocations } = catalogLocationsQuery.data || { data: [] };

  useEffect(() => {
    if (catalogLocations.length) {
      setLocationId(catalogLocations[0].id);
      setVisibility('visible');
    }
  }, [catalogLocations]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (locationId) {
      setPage(1);
      setPageSize(10);
    }
  }, [locationId]);

  const productsQuery = useCatalogItems(catalogId, {
    page,
    pageSize,
    refetchInterval: 5000,
    visibility: visibility,
    locationId: locationId,
  });

  if (!catalogsQuery.data) {
    return (
      <Page>
        <LoadingModal isOpen={true} />
      </Page>
    );
  }
  const { data: catalogs } = catalogsQuery.data;
  let { data: products, total: productsTotal } = productsQuery.data || {
    data: [],
    total: 0,
  };

  const catalog = catalogs.find((c) => c.id === catalogId);

  if (!catalog) {
    return null;
  }

  if (productsTotal > 0 && total === 0) setTotal(productsTotal);

  const getProductsSubtitleText = () => {
    if (productsTotal === 0) {
      return 'Pending Items';
    }

    if (productsTotal === 1) {
      return '1 item';
    }

    return `${productsTotal} items`;
  };

  const productsSubtitleText = `${capitalize(
    catalog?.connection?.connector?.name || '',
  )}${'  '}•${' '}${getProductsSubtitleText()}`;

  const handlePageSizeChange = (page: number) => {
    setPage(page);
  };

  const count = Math.ceil(productsTotal / pageSize);
  const locationHasNoItem = total > 0 && productsTotal === 0;

  const pendingTitle = 'Hold Tight!';
  const pendingItemsDescrip =
    'We’re importing your items from your POS. This may take a few minutes.';
  const noItemTitle = 'Oops, no items found';
  const noItemDescrip =
    'We can’t seem to find any active items for this location. If items are missing that should be visible here, make sure they are set to be active for this location in your POS.';

  // Update last synced time
  const isLoadingProduct = productsQuery.isFetching;
  let now = moment();
  if (!isLoadingProduct && now !== moment()) now = moment();

  return (
    <Page>
      <PageLayout title={<PageTitle title="Catalogs" />} className="text-black">
        <Card className="h-full">
          <div className="px-4 flex flex-col h-full">
            <header className="flex items-center">
              <div className="grid gap-1 py-4 mr-auto">
                <Text as="h2" variant="subheader">
                  {catalog.name}
                </Text>
                <Text as="h3" className="text-gray-400 font-regular text-lg">
                  {productsSubtitleText}
                </Text>
              </div>
              <div>
                <Select
                  label="Location"
                  onSelectionChange={(key) => setLocationId(key as string)}
                  selectedKey={locationId}
                >
                  {catalogLocations.map((c) => (
                    <SelectItem key={c.id} id={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </header>

            <div className="py-1 mb-1">
              {!isLoadingProduct ? (
                <Text className="text-gray-600 font-regular text-lg flex justify-end">
                  <img
                    className="mr-1"
                    src={rotateRightSvg}
                    onClick={() => productsQuery.refetch()}
                    alt=""
                  />
                  {`Last Synced: ${now.format('MMMM D, YYYY')} at ${now.format(
                    'h:mm a',
                  )}`}
                </Text>
              ) : (
                <Text className="text-gray-600 font-regular text-lg flex justify-end">
                  Syncing in progress...
                </Text>
              )}
            </div>

            <div className="rounded-2xl bg-gray-100 px-8 py-3 mb-1">
              <Text className="text-gray-600 font-semibold text-xl">{`ITEMS (${productsTotal})`}</Text>
            </div>

            <div className="px-9 flex flex-col gap-1 flex-1 mb-3">
              {productsTotal > 0 ? (
                products.map((product) => (
                  <div className="flex py-2 items-center" key={product.id}>
                    <div className="bg-blue-50 rounded-md text-blue-600 size-5 flex items-center justify-center mr-3">
                      <Basket />
                    </div>
                    <Text className="text-lg">{product.name}</Text>
                  </div>
                ))
              ) : (
                // There isn't a way to distinguish between 0 items and items that still haven't been synced yet.
                //  It's rare to actually have 0 items, so we accommodate for the most common use case which is items still syncing.
                <div className="flex items-center justify-center flex-col h-full">
                  <img
                    src={
                      isLoadingProduct
                        ? zeroItemsSvg
                        : locationHasNoItem
                        ? noItemsSvg
                        : zeroItemsSvg
                    }
                    alt="zero items placeholder"
                    className="mb-2"
                  />
                  <Text as="p" variant="smallSubheader" className="mb-2">
                    {isLoadingProduct
                      ? pendingTitle
                      : locationHasNoItem
                      ? noItemTitle
                      : pendingTitle}
                  </Text>
                  <Text alignment="center">
                    {isLoadingProduct
                      ? pendingItemsDescrip
                      : locationHasNoItem
                      ? noItemDescrip
                      : pendingItemsDescrip}
                  </Text>
                </div>
              )}
            </div>

            <div className="flex items-center justify-center relative">
              <Pagination
                count={count}
                onChange={handlePageSizeChange}
                page={page}
              />
              <div className="absolute right-0 flex items-center">
                <PageSizeSelect onChange={setPageSize} pageSize={pageSize} />
              </div>
            </div>
          </div>
        </Card>
      </PageLayout>
    </Page>
  );
};

export default ProductHubCatalogDetailsPage;
